import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, ShieldCheck, MessageCircle, Instagram, Calendar, ArrowRight, Sparkles } from "lucide-react";
import SEOHead from "@/components/site/SEOHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Contacts ---------------------------------------------------------------
const WHATSAPP_NUMBER = "262693482110";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Bonjour EM Group, je viens de m'inscrire à l'offre découverte réseaux sociaux."
)}`;
const INSTAGRAM_URL = "https://instagram.com/emgroup.digital";

// --- Helpers ----------------------------------------------------------------
const MONTHS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

function formatFr(date) {
  return `${date.getDate()} ${MONTHS_FR[date.getMonth()]} ${date.getFullYear()}`;
}

export default function ConfirmationDecouverteRS() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [confettiOn, setConfettiOn] = useState(true);
  const [serverFirstChargeDate, setServerFirstChargeDate] = useState(null);

  // Fallback = today + 60d (used only until the server date is fetched, or if fetch fails)
  const clientFallbackDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 60);
    return formatFr(d);
  }, []);

  const firstChargeDate = serverFirstChargeDate || clientFallbackDate;

  useEffect(() => {
    // Fetch the authoritative first_charge_date from the checkout-status endpoint
    // (based on transaction.created_at + trial_period_days) so it stays accurate
    // even if the user opens this page hours/days after checkout.
    if (!sessionId) return;
    axios
      .get(`${API}/checkout/status/${sessionId}`)
      .then((r) => {
        if (r.data && r.data.first_charge_date) {
          setServerFirstChargeDate(r.data.first_charge_date);
        }
      })
      .catch(() => {
        /* silent — client fallback covers the display */
      });
  }, [sessionId]);

  useEffect(() => {
    // Fire conversion tracking si dispo (Meta Pixel / GA)
    if (typeof window !== "undefined") {
      try {
        if (window.fbq) window.fbq("track", "CompleteRegistration", { value: 0, currency: "EUR" });
        if (window.gtag) window.gtag("event", "subscription_trial_start", { event_category: "decouverte_rs", value: 0 });
      } catch (_e) { /* noop */ }
    }
    const t = setTimeout(() => setConfettiOn(false), 4500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white relative overflow-hidden" data-testid="confirmation-decouverte-rs-page">
      <SEOHead
        title="Inscription confirmée — Offre découverte EM Group"
        description="Votre inscription à l'offre découverte EM Group est confirmée. Aucun prélèvement pendant 60 jours."
        canonical="https://emgroupdigital.fr/decouverte-rs/confirmation"
        locale="fr_FR"
        htmlLang="fr"
        robots="noindex, nofollow"
      />

      {/* Ambient gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#E52431]/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-emerald-500/[0.08] blur-[120px]" />
      </div>

      {/* Confetti dots (CSS only) */}
      {confettiOn && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
          {Array.from({ length: 18 }).map((_, i) => (
            <span
              key={i}
              className="absolute block w-1.5 h-1.5 rounded-full animate-confetti"
              style={{
                left: `${(i * 5.5) + 4}%`,
                top: `-${10 + (i % 5) * 8}%`,
                background: ["#E52431", "#FF6A75", "#FFB4BB", "#10B981", "#FFFFFF"][i % 5],
                animationDelay: `${(i * 0.18).toFixed(2)}s`,
              }}
            />
          ))}
        </div>
      )}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(540deg); opacity: 0.2; }
        }
        .animate-confetti { animation: confetti-fall 3.6s linear forwards; }
      `}</style>

      {/* Header */}
      <header className="relative z-10 px-6 md:px-10 pt-6 md:pt-8">
        <Link to="/" data-testid="confirmation-logo" className="inline-flex items-center gap-2 group">
          <img src="/brand/logo-em-group.png" alt="EM Group" className="w-10 h-10 rounded-full" />
          <span className="font-outfit text-base font-medium tracking-tight text-white/90 group-hover:text-white transition">EM Group</span>
        </Link>
      </header>

      <main className="relative z-10 px-6 md:px-10 py-12 md:py-20 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] uppercase tracking-[0.15em] font-bold">
          <Sparkles size={11} /> Inscription confirmée
        </div>

        <h1 className="mt-6 font-outfit text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
          Bienvenue dans l&apos;<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E52431] to-[#FF6A75] font-medium">offre découverte</span> EM&nbsp;Group&nbsp;!
        </h1>

        <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed max-w-2xl">
          Votre inscription est confirmée. Voici ce qui vous attend maintenant&nbsp;:
        </p>

        <ul className="mt-10 space-y-4" data-testid="confirmation-checklist">
          <li className="flex items-start gap-4 p-5 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.05]">
            <div className="w-10 h-10 shrink-0 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
              <ShieldCheck size={20} className="text-emerald-300" />
            </div>
            <div className="flex-1">
              <div className="text-base md:text-lg text-white font-medium leading-snug">Aucun prélèvement pendant 60 jours</div>
              <p className="mt-1 text-sm text-white/65 leading-relaxed">
                Votre carte est enregistrée en toute sécurité chez Stripe, mais elle ne sera débitée qu&apos;à partir de J+60.
              </p>
            </div>
          </li>

          <li className="flex items-start gap-4 p-5 rounded-2xl border border-[#E52431]/30 bg-gradient-to-br from-[#E52431]/[0.06] to-transparent" data-testid="confirmation-first-charge">
            <div className="w-10 h-10 shrink-0 rounded-xl bg-[#E52431]/15 border border-[#E52431]/40 flex items-center justify-center">
              <Calendar size={20} className="text-[#FFB4BB]" />
            </div>
            <div className="flex-1">
              <div className="text-base md:text-lg text-white font-medium leading-snug">Premier paiement le <span className="text-[#FFB4BB]">{firstChargeDate}</span></div>
              <p className="mt-1 text-sm text-white/65 leading-relaxed">
                Vous recevrez un email de rappel 7 jours avant. Annulation possible à tout moment depuis votre espace client.
              </p>
            </div>
          </li>

          <li className="flex items-start gap-4 p-5 rounded-2xl border border-white/10 bg-white/[0.03]">
            <div className="w-10 h-10 shrink-0 rounded-xl bg-white/[0.08] border border-white/15 flex items-center justify-center">
              <Check size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-base md:text-lg text-white font-medium leading-snug">Un membre de l&apos;équipe vous contacte sous 24h</div>
              <p className="mt-1 text-sm text-white/65 leading-relaxed">
                Pour cadrer ensemble votre stratégie, récupérer vos accès et démarrer votre accompagnement réseaux sociaux.
              </p>
            </div>
          </li>
        </ul>

        {/* Contact options */}
        <div className="mt-12 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-6 md:p-8" data-testid="confirmation-contact-block">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold">— Des questions&nbsp;?</div>
          <h2 className="mt-2 font-outfit text-2xl md:text-3xl font-light tracking-tight">Écrivez-nous directement.</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmation-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.05] hover:bg-emerald-500/[0.12] text-white px-5 py-3.5 text-sm font-medium transition"
            >
              <MessageCircle size={16} className="text-emerald-300" /> WhatsApp&nbsp;: +262 693 48 21 10
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmation-instagram"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#E1306C]/35 bg-[#E1306C]/[0.05] hover:bg-[#E1306C]/[0.12] text-white px-5 py-3.5 text-sm font-medium transition"
            >
              <Instagram size={16} className="text-[#F5A6C4]" /> @emgroup.digital
            </a>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/"
            data-testid="confirmation-home-link"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition"
          >
            Retour à l&apos;accueil <ArrowRight size={14} />
          </Link>
          {sessionId && (
            <p className="mt-6 text-[10px] text-white/30 font-mono break-all max-w-md mx-auto leading-relaxed">
              Réf. Stripe&nbsp;: {sessionId}
            </p>
          )}
        </div>
      </main>

      <footer className="relative z-10 px-6 md:px-10 py-8 text-[11px] text-white/35 text-center">
        © {new Date().getFullYear()} EM Group Digital — Tous droits réservés | <a href="mailto:contact@emgroupdigital.fr" className="hover:text-white/60 transition">contact@emgroupdigital.fr</a>
      </footer>
    </div>
  );
}
